/**
 * Match detail page with dark mode support
 */

import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { useMatch } from '../hooks/useMatches';
import { formatMatchDate, formatTime, getStageDisplayName, getStatusDisplay, cn } from '../lib/utils';

export default function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: match, isLoading } = useMatch(parseInt(id || '0', 10));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-64 skeleton rounded-xl" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="text-center py-12">
        <div className="w-14 h-14 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-7 h-7 text-neutral-400 dark:text-neutral-500" />
        </div>
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
          Partida nao encontrada
        </h3>
        <Link
          to="/jogos"
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
        >
          Voltar para jogos
        </Link>
      </div>
    );
  }

  const status = getStatusDisplay(match.status);
  const isFinished = match.status === 'finished';
  const isLive = match.status === 'live';

  return (
    <div className="space-y-6">
      <Link
        to="/jogos"
        className={cn(
          'inline-flex items-center gap-2 transition-colors',
          'text-neutral-600 dark:text-neutral-400',
          'hover:text-neutral-900 dark:hover:text-white'
        )}
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar para jogos
      </Link>

      <div className="card overflow-hidden">
        {/* Match Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/80">{getStageDisplayName(match.stage)}</span>
            <span className={cn('badge', status.color)}>
              {isLive && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-1.5" />}
              {status.label}
            </span>
          </div>

          <div className="flex items-center justify-center gap-8">
            <div className="text-center flex-1">
              <div className="text-3xl font-bold mb-2">{match.homeTeam}</div>
            </div>

            <div className="text-center">
              {isFinished || isLive ? (
                <div className="bg-white/20 rounded-xl px-6 py-4">
                  <span className="text-4xl font-bold">{match.homeScore}</span>
                  <span className="text-2xl mx-3">-</span>
                  <span className="text-4xl font-bold">{match.awayScore}</span>
                </div>
              ) : (
                <div className="text-2xl font-bold">VS</div>
              )}
            </div>

            <div className="text-center flex-1">
              <div className="text-3xl font-bold mb-2">{match.awayTeam}</div>
            </div>
          </div>
        </div>

        {/* Match Info */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className={cn(
              'flex items-center gap-3 p-4 rounded-lg',
              'bg-neutral-50 dark:bg-neutral-800'
            )}>
              <Calendar className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Data</p>
                <p className="font-medium text-neutral-900 dark:text-white">{formatMatchDate(match.matchDate)}</p>
              </div>
            </div>

            <div className={cn(
              'flex items-center gap-3 p-4 rounded-lg',
              'bg-neutral-50 dark:bg-neutral-800'
            )}>
              <Clock className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Horario</p>
                <p className="font-medium text-neutral-900 dark:text-white">{formatTime(match.matchDate)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
